import * as React from 'react';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import driverinfosecurity from '../../utils/DriverInfoSecurity';
import { flags } from '../../constants/flags';
import * as i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import { LANGUAGE } from '../../constants/storageKeys';
import { useLogin } from '../../hooks/useLogin';
import { useLanguages, useLanguagesMatch } from '../../hooks/useLanguages';
import logger from '../../utils/logger';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function Login(props: RouteComponentProps<{}>) {
    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [submitted, setSubmitted] = React.useState(false);
    const [showErrorMessage, setShowErrorMessage] = React.useState(false);

    const loginMutation = useLogin();
    const { data: languageCodes } = useLanguages();
    const languagesMatch = useLanguagesMatch();

    React.useEffect(() => {
        if (languageCodes && languageCodes.length > 0 && !languagesMatch) {
            throw new Error('Language mismatch between client and server!');
        }
    }, [languageCodes, languagesMatch]);

    React.useEffect(() => {
        if (loginMutation.isSuccess) {
            logger.info(`Login succeeded for user: ${username}`);
        }
    }, [loginMutation.isSuccess]);

    React.useEffect(() => {
        if (loginMutation.isError) {
            logger.warn(`Login failed for user: ${username}`);
        }
    }, [loginMutation.isError]);

    if (driverinfosecurity.hasValidToken()) {
        const { from }: any = (props.location.state as any) || { from: { pathname: '/' } };
        return <Redirect to={from} />;
    }

    if (loginMutation.isSuccess) {
        const { from }: any = (props.location.state as any) || { from: { pathname: '/' } };
        return <Redirect to={from} />;
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setSubmitted(true);
        setShowErrorMessage(true);
        if (username && password) {
            logger.info(`Login attempt for user: ${username}`);
            loginMutation.mutate({ username, password });
        }
    }

    function changeLanguage(lang: string) {
        i18next.changeLanguage(lang);
        localStorage[LANGUAGE] = lang;
    }

    const loginError = loginMutation.isError ? 'Failed to log in with user id ' + username : '';

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'stretch'
        }}>
            {/* Left graphic */}
            <Box sx={{ flex: '0 0 auto', display: { xs: 'none', md: 'flex' }, alignItems: 'center', overflow: 'hidden', maxWidth: 320 }}>
                <img
                    src={new URL('../../img/bg-graphic-x.png', import.meta.url).href}
                    style={{ maxWidth: '100%', objectFit: 'cover' }}
                    alt=""
                />
            </Box>

            {/* Right form panel */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', px: { xs: 3, md: 8 }, py: 6, maxWidth: 520 }}>
                <Box sx={{ mb: 5 }}>
                    <img src={new URL('../../img/logo2.png', import.meta.url).href} alt="Logo" />
                </Box>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        placeholder={i18next.t('login.username')}
                        name="username"
                        value={username}
                        onChange={e => { setUsername(e.target.value); setShowErrorMessage(false); }}
                        error={submitted && !username}
                        helperText={submitted && !username ? <span className="errorColor">{i18next.t('login.requireusername')}</span> : ''}
                        variant="standard"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonOutlineIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                    </InputAdornment>
                                ),
                                sx: { color: 'white' }
                            }
                        }}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255,255,255,0.4)' },
                            '& .MuiInput-underline:hover:before': { borderBottomColor: 'rgba(255,255,255,0.7)' },
                            '& .MuiInput-underline:after': { borderBottomColor: 'white' },
                            '& input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 },
                            '& .MuiFormHelperText-root': { color: 'rgba(255,150,150,1)' }
                        }}
                    />

                    <TextField
                        type="password"
                        placeholder={i18next.t('login.password')}
                        name="password"
                        value={password}
                        onChange={e => { setPassword(e.target.value); setShowErrorMessage(false); }}
                        error={submitted && !password}
                        helperText={submitted && !password ? <span className="errorColor">{i18next.t('login.requirepassword')}</span> : ''}
                        variant="standard"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                    </InputAdornment>
                                ),
                                sx: { color: 'white' }
                            }
                        }}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255,255,255,0.4)' },
                            '& .MuiInput-underline:hover:before': { borderBottomColor: 'rgba(255,255,255,0.7)' },
                            '& .MuiInput-underline:after': { borderBottomColor: 'white' },
                            '& input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 },
                            '& .MuiFormHelperText-root': { color: 'rgba(255,150,150,1)' }
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        fullWidth
                        size="large"
                        disabled={loginMutation.isPending}
                        sx={{ mt: 1, height: 48 }}
                        startIcon={loginMutation.isPending ? <CircularProgress size={18} color="inherit" /> : undefined}
                    >
                        {i18next.t('login.button')}
                    </Button>

                    {loginError && showErrorMessage && password && username && (
                        <Alert severity="error" className="alert-danger" sx={{ mt: 1 }}>{loginError}</Alert>
                    )}

                    <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        onClick={() => { logger.info('Login attempt via Microsoft Entra'); window.location.href = '/auth/entra/login'; }}
                        sx={{ mt: 1, height: 48, borderColor: 'rgba(255,255,255,0.5)', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        Sign in with Microsoft
                    </Button>
                </Box>

                <Box sx={{ mt: 5, display: 'flex', gap: 2.5 }}>
                    {flags.map((lang, index) => (
                        <a key={index} href={window.location.href} onClick={() => changeLanguage(lang.cultureCode)} style={{ cursor: 'pointer' }}>
                            <span
                                title={i18next.t(lang.languageTag)}
                                className={lang.flag + 'loginFlags'}
                                style={{ display: 'block', height: 30, width: 40 }}
                            />
                        </a>
                    ))}
                </Box>

                <Box sx={{ mt: 4, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <InfoOutlinedIcon sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.3, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {i18next.t('cookie.text')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default withTranslation('translations')(Login);
